const BASE = import.meta.env.VITE_API_BASE_URL || "";
const AUTH_DISABLED = import.meta.env.VITE_AUTH_DISABLED === "true";
const BYPASS_USER = "dev-user";

let tokenGetter: (() => string | null) | null = null;

export function setTokenGetter(fn: () => string | null) {
  tokenGetter = fn;
}

function headers(): Record<string, string> {
  const h: Record<string, string> = {};
  if (AUTH_DISABLED) {
    h["X-User-Id"] = BYPASS_USER;
  } else {
    const t = tokenGetter?.() ?? localStorage.getItem("trustycert_token");
    if (t) h["Authorization"] = `Bearer ${t}`;
  }
  return h;
}

async function apiFetch(path: string, opts: RequestInit = {}): Promise<Response> {
  const resp = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: { ...headers(), ...opts.headers },
  });
  if (resp.status === 401) {
    window.dispatchEvent(new CustomEvent("auth:unauthorized"));
  }
  return resp;
}

async function jsonFetch<T = unknown>(path: string, opts: RequestInit = {}): Promise<T> {
  const resp = await apiFetch(path, opts);
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }));
    throw new Error(body.error || body.detail || `Errore ${resp.status}`);
  }
  return resp.json();
}

// ── Documents ──

export interface DocField {
  name: string;
  type: string;
  required: boolean;
}

export interface EnrichedField {
  field_name: string;
  label: string;
  description: string;
  type: string;
  format_hint: string | null;
  required: boolean;
  order: number;
}

export interface UploadResult {
  document_id: string;
  filename: string;
  fields: DocField[];
  enriched_fields: EnrichedField[] | null;
  field_count: number;
  pages: number;
  signature_field_name: string | null;
  signature_fields: string[];
}

export interface Document {
  id: string;
  user_id: string;
  filename: string;
  file_path: string;
  acroform_fields: DocField[] | null;
  enriched_fields: EnrichedField[] | null;
  signature_field_name: string | null;
  signature_fields: string[] | null;
  created_at: string;
}

/** Error thrown by uploadDocument carrying a machine-readable backend code. */
export class UploadError extends Error {
  code: string;
  status: number;
  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = "UploadError";
    this.code = code;
    this.status = status;
  }
}

export async function uploadDocument(file: File): Promise<UploadResult> {
  const form = new FormData();
  form.append("file", file);
  const resp = await apiFetch("/api/documents/upload", { method: "POST", body: form });
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }));
    throw new UploadError(
      body.error || `Errore upload: ${resp.status}`,
      body.code || "upload_error",
      resp.status,
    );
  }
  return resp.json();
}

export async function getDocument(id: string): Promise<Document> {
  return jsonFetch(`/api/documents/${id}`);
}

export async function getDocuments(): Promise<Document[]> {
  return jsonFetch("/api/documents");
}

export async function deleteDocument(id: string): Promise<void> {
  await jsonFetch(`/api/documents/${id}`, { method: "DELETE" });
}

// ── Recognition types ──

export type RecognitionType = "FEA_SELFID" | "FEA_SPID" | "FEA_CIE";

export interface RecognitionOption {
  value: RecognitionType;
  label: string;
  description: string;
  icon: string;
}

export const RECOGNITION_TYPES: RecognitionOption[] = [
  {
    value: "FEA_SELFID",
    label: "Self ID",
    description: "Riconoscimento automatico con selfie e documento d'identità",
    icon: "photo_camera",
  },
  {
    value: "FEA_SPID",
    label: "SPID",
    description: "Identificazione tramite SPID (Sistema Pubblico di Identità Digitale)",
    icon: "verified_user",
  },
  {
    value: "FEA_CIE",
    label: "CIE",
    description: "Identificazione tramite Carta d'Identità Elettronica (NFC)",
    icon: "contactless",
  },
];

// ── Sessions ──

export interface CreateSessionPayload {
  document_id: string;
  recognition_type: RecognitionType;
  recipient: { name: string; surname: string; phone: string };
  prefilled_data: Record<string, string>;
  fields_to_collect: string[];
  company_name?: string;
  operator_name?: string;
}

export interface CreateSessionResult {
  session_id: string;
  status: string;
}

export interface Session {
  id: string;
  user_id: string;
  document_id: string;
  recipient_name: string;
  recipient_phone: string;
  prefilled_data: Record<string, string> | null;
  fields_to_collect: string[] | null;
  collected_data: Record<string, string> | null;
  compiled_file_path: string | null;
  signed_file_path: string | null;
  dossier_id: string | null;
  signing_url: string | null;
  top_token: string | null;
  agent_thread_id: string | null;
  recognition_type: string | null;
  status: string;
  company_name: string | null;
  operator_name: string | null;
  created_at: string;
  updated_at: string;
  signed_at: string | null;
}

export async function createSession(payload: CreateSessionPayload): Promise<CreateSessionResult> {
  return jsonFetch("/api/sessions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export async function getSessions(): Promise<Session[]> {
  return jsonFetch("/api/sessions");
}

export async function getSession(id: string): Promise<Session> {
  return jsonFetch(`/api/sessions/${id}`);
}

export async function deleteSession(id: string): Promise<void> {
  await jsonFetch(`/api/sessions/${id}`, { method: "DELETE" });
}

export async function retrySession(id: string): Promise<Session> {
  return jsonFetch(`/api/sessions/${id}/retry`, { method: "POST" });
}

export async function retryTopSession(id: string): Promise<Session> {
  return jsonFetch(`/api/sessions/${id}/retry-top`, { method: "POST" });
}

export async function getSignedDoc(id: string): Promise<Blob> {
  const resp = await apiFetch(`/api/sessions/${id}/signed-doc`);
  if (!resp.ok) throw new Error("Documento firmato non disponibile");
  return resp.blob();
}

// ── Providers ──

export interface Provider {
  id: string;
  name: string;
  description: string | null;
  api_key_prefix: string;
  allowed_products: string[];
  allowed_recognition_types: string[];
  webhook_url: string | null;
  expires_at: string | null;
  active: boolean;
  created_at: string;
  session_count: number;
  last_session_at: string | null;
}

export interface ProviderWithSessions extends Provider {
  sessions: Session[];
}

export interface CreateProviderPayload {
  name: string;
  description?: string;
  allowed_products: string[];
  allowed_recognition_types: string[];
  webhook_url?: string;
  expires_in_days?: number;
}

export interface CreateProviderResult extends Provider {
  api_key: string;
}

export async function getProviders(): Promise<Provider[]> {
  return jsonFetch("/api/admin/providers");
}

export async function getProvider(id: string): Promise<ProviderWithSessions> {
  return jsonFetch(`/api/admin/providers/${id}`);
}

export async function createProvider(payload: CreateProviderPayload): Promise<CreateProviderResult> {
  return jsonFetch("/api/admin/providers", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export async function updateProvider(
  id: string,
  payload: Partial<CreateProviderPayload>,
): Promise<Provider> {
  return jsonFetch(`/api/admin/providers/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export async function rotateProviderKey(id: string): Promise<{ api_key: string }> {
  return jsonFetch(`/api/admin/providers/${id}/rotate-key`, { method: "POST" });
}

export interface DeleteProviderResult {
  deleted: boolean;
  sessions_deleted: number;
}

export async function deleteProvider(id: string): Promise<DeleteProviderResult> {
  return jsonFetch(`/api/admin/providers/${id}`, { method: "DELETE" });
}

export async function deactivateProvider(id: string): Promise<{ deactivated: boolean }> {
  return jsonFetch(`/api/admin/providers/${id}/deactivate`, { method: "POST" });
}
